import {
  type ModalHook,
  useControlledModalState,
} from '@leland-dev/leland-ui-library';
import React, { type FC, useCallback } from 'react';

import { type Scalars } from '../../__generated-gql-types__/globalTypes';
import { getBatchResponseStart } from '../../utils/pagination';
import {
  BASE_ALL_REVIEWS_MODAL_PAGE_META,
  BaseAllReviewsModal,
} from '../coach/modals/BaseAllReviewsModal';

import { useAllPackageReviewsModalReviewsForPackageQuery } from './__generated-gql-types__/AllPackageReviewsModal.generated';

interface AllPackageReviewsModalHookProps {
  categoryUrn: Scalars['CategoryUrn']['output'];
}

interface AllPackageReviewsModalProps extends AllPackageReviewsModalHookProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const AllPackageReviewsModalComponent: FC<AllPackageReviewsModalProps> = ({
  open,
  setOpen,
  categoryUrn,
}) => {
  const {
    data: allPackageReviews,
    loading: allPackageReviewsLoading,
    fetchMore,
  } = useAllPackageReviewsModalReviewsForPackageQuery({
    variables: {
      categoryUrn: categoryUrn,
      start: getBatchResponseStart(
        BASE_ALL_REVIEWS_MODAL_PAGE_META.defaultPage,
        BASE_ALL_REVIEWS_MODAL_PAGE_META.pageSize,
      ),
      count: BASE_ALL_REVIEWS_MODAL_PAGE_META.pageSize,
    },
  });

  const onFetchMore = useCallback(
    (page: number) =>
      fetchMore({
        variables: {
          start: getBatchResponseStart(
            page,
            BASE_ALL_REVIEWS_MODAL_PAGE_META.pageSize,
          ),
        },
      }),
    [fetchMore],
  );

  const resultsToUse = allPackageReviews?.reviewsForPackage.results;

  return (
    <BaseAllReviewsModal
      open={open}
      setOpen={setOpen}
      title="All Package Reviews"
      loading={allPackageReviewsLoading}
      total={allPackageReviews?.reviewsForPackage.total ?? 0}
      fetchMore={onFetchMore}
      reviews={resultsToUse}
    />
  );
};

export const useAllPackageReviewsModal: ModalHook<
  AllPackageReviewsModalHookProps
> = () => {
  const [open, setOpen] = useControlledModalState();
  const AllPackageReviewsModal: FC<AllPackageReviewsModalProps> = (props) =>
    open ? (
      <AllPackageReviewsModalComponent
        {...props}
        open={open}
        setOpen={setOpen}
      />
    ) : null;

  return { open, setOpen, Modal: AllPackageReviewsModal };
};
